import React from 'react';
import { CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const MatchingQuestion = ({
  question,
  result,
  currentAnswer,
  submittedFirstTime,
  showExplanation,
  canRetry,
  onAnswerChange,
  onToggleExplanation,
  allQuestions, // Pass all questions to find shared options
  allAnswers = {} // Pass all current answers to check what options are already used
}) => {
  // Determine if this is correct/incorrect based on result
  const isCorrect = result?.isCorrect;
  const isAnswered = currentAnswer !== undefined && currentAnswer !== '';

  // Get status styling
  const getStatusStyling = () => {
    if (!submittedFirstTime) return '';
    if (isCorrect) return 'border-green-500 bg-green-50';
    if (!isCorrect && !canRetry) return 'border-red-500 bg-red-50';
    if (canRetry) return 'border-orange-500 bg-orange-50';
    return '';
  };

  // Get status icon
  const getStatusIcon = () => {
    if (!submittedFirstTime) return null;
    if (isCorrect) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (!isCorrect) return <XCircle className="w-5 h-5 text-red-600" />;
    return null;
  };

  // Convert options array to option-value pairs with Roman numerals
  const getOptionsWithValues = () => {
    const romanNumerals = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];

    // Lấy từ question.word_bank nếu không có options
    let optionsToUse = question.word_bank;

    if (typeof optionsToUse === "string") {
      try {
        optionsToUse = JSON.parse(optionsToUse);
      } catch {
        optionsToUse = [];
      }
    }

    // Nếu current question không có, tìm từ các câu matching khác
    if (!optionsToUse && allQuestions) {
      const matchingQuestionWithOptions = allQuestions.find(q =>
        q.question_type === 'matching' && (q.word_bank)
      );
      if (matchingQuestionWithOptions) {
        optionsToUse = matchingQuestionWithOptions.word_bank;
      }
    }

    // fallback nếu cần
    if (!optionsToUse && question.section === "Job Review Analysis") {
      optionsToUse = [
        "Qualifications",
        "Accountabilities",
        "word Environment",
        "word Schedule",
        "Professional Development",
        "Collaboration & Teamword",
        "Student Support"
      ];
    }

    if (!optionsToUse || !Array.isArray(optionsToUse)) {
      return [];
    }

    return optionsToUse.map((option, index) => ({
      value: romanNumerals[index],
      label: option
    }));
  };


  // Get used options by other questions (excluding current question)
  const getUsedOptions = () => {
    if (!allAnswers || !allQuestions) return new Set();

    const usedOptions = new Set();

    // Find all matching questions that share the same option pool
    // This could be questions in the same section or all matching questions
    const relatedMatchingQuestions = allQuestions.filter(q => {
      // Include if it's a matching question and not the current question
      if (q.question_type !== 'matching' || q.id === question.id) return false;

      // If questions have sections, only include same section
      if (question.section && q.section) {
        return q.section === question.section;
      }

      // If no sections, include all matching questions
      return true;
    });

    // Check what options are already used by other questions
    relatedMatchingQuestions.forEach(q => {
      const answerKey = `q${q.id}`;
      const answer = allAnswers[answerKey];
      if (answer && answer !== '') {
        usedOptions.add(answer);
      }
    });

    return usedOptions;
  };

  const optionsWithValues = getOptionsWithValues();
  const usedOptions = getUsedOptions();

  // Debug logging (remove in production)
  // console.log(`Question ${question.id}:`, {
  //   currentAnswer,
  //   usedOptions: Array.from(usedOptions),
  //   allAnswers,
  //   relatedQuestions: allQuestions?.filter(q =>
  //     q.type === 'matching' && q.id !== question.id
  //   ).map(q => ({ id: q.id, answer: allAnswers[`q${q.id}`] }))
  // });

  // Early return if no options available
  if (!optionsWithValues || optionsWithValues.length === 0) {
    return (
      <div className="p-4 border rounded-lg border-red-200 bg-red-50">
        <div className="text-red-700">
          <strong>Error:</strong> No options available for this matching question.
        </div>
      </div>
    );
  }

  // Handle answer change
  const handleAnswerChange = (value) => {
    if (!submittedFirstTime || canRetry) {
      onAnswerChange(value);
    }
  };

  // Check if option is disabled
  const isDisabled = submittedFirstTime && !canRetry;

  // Check if option is available (not used by other questions)
  const isOptionAvailable = (optionValue) => {
    // If it's the current answer, always allow it
    if (optionValue === currentAnswer) {
      return true;
    }
    // Otherwise check if it's not used by other questions
    return !usedOptions.has(optionValue);
  };

  return (
    <div className={`p-4 border rounded-lg ${getStatusStyling()}`}>
      {/* Question Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-semibold text-gray-600">
            Question {question.question_number ?? question.id}
          </span>
          {getStatusIcon()}
          {submittedFirstTime && (
            <span className={`text-xs px-2 py-1 rounded ${isCorrect ? 'bg-green-100 text-green-700' :
              canRetry ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
              }`}>
              {canRetry ? 'Can retry' : isCorrect ? 'Correct' : 'Incorrect'}
            </span>
          )}
        </div>

        {submittedFirstTime && (
          <button
            onClick={onToggleExplanation}
            className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm"
          >
            {showExplanation ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            <span>{showExplanation ? 'Hide' : 'Show'} Explanation</span>
          </button>
        )}
      </div>

      {/* Section and Instruction */}
      {question.section && (
        <div className="mb-3">
          <span className="text-sm font-semibold text-blue-700 bg-blue-50 px-2 py-1 rounded">
            {question.section}
          </span>
        </div>
      )}


      {/* Question Text and Answer Selection */}
      <div className="mb-4">
        <div className="flex items-center space-x-3 flex-wrap">
          <span className="text-gray-800 font-medium text-base">
            <strong>{question.question_text}</strong> matches with:
          </span>

          <select
            value={currentAnswer || ''}
            onChange={(e) => handleAnswerChange(e.target.value)}
            disabled={isDisabled}
            className={`border rounded px-3 py-2 text-sm min-w-[200px] ${isDisabled
              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
              : 'bg-white border-gray-300 hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
              } ${isAnswered ? 'font-medium' : ''}`}
          >
            <option value="">-- Select an option --</option>
            {optionsWithValues.map((option) => {
              const isAvailable = isOptionAvailable(option.value);
              return (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={!isAvailable}
                  style={{
                    color: !isAvailable ? '#9ca3af' : 'inherit',
                    fontStyle: !isAvailable ? 'italic' : 'normal'
                  }}
                >
                  {option.label}
                  {!isAvailable && option.value !== currentAnswer ? ' (Already used)' : ''}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Show only user's answer after submission, not the correct answer */}
      {submittedFirstTime && result && (
        <div className="mb-3 text-sm">
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">
              <strong>Your answer:</strong>
              {typeof result.selected === "object"
                ? JSON.stringify(result.selected)
                : (result.selected || 'Not answered')}

              {result.selected && optionsWithValues.find(opt => opt.value === result.selected) && (
                <span className="text-gray-500 ml-1">
                  ({optionsWithValues.find(opt => opt.value === result.selected).label})
                </span>
              )}
            </span>

            {/* Status indicator without showing correct answer */}
            <span className={`font-medium ${isCorrect ? 'text-green-600' : 'text-red-600'
              }`}>
              {isCorrect ? '✓ Correct' : '✗ Incorrect'}
            </span>
          </div>
        </div>
      )}

      {/* Retry message */}
      {canRetry && (
        <div className="mb-3 p-2 bg-orange-50 border border-orange-200 rounded text-sm text-orange-700">
          💡 You can change your answer and try again.
        </div>
      )}

      {/* Explanation */}
      {showExplanation && submittedFirstTime && question.feedback && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded">
          <div className="text-sm font-semibold text-gray-700 mb-2">📖 Explanation:</div>

          <div className="text-sm text-gray-700">
            {isCorrect && question.feedback.correct ? (
              <div className="text-green-700 bg-green-50 p-2 rounded">
                ✅ {question.feedback.correct}
              </div>
            ) : (
              <div className="text-red-700 bg-red-50 p-2 rounded">
                ❌ {typeof question.feedback.incorrect === "object"
                  ? JSON.stringify(question.feedback.incorrect)
                  : (question.feedback.incorrect || 'This answer is incorrect...')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress indicator */}
      <div className="mt-3 text-xs text-gray-500">
        {submittedFirstTime ? (
          <span>
            Attempt {result?.attempts || 1}/2
            {result?.score !== undefined && ` | Score: ${result.score}`}
          </span>
        ) : (
          <span></span>
        )}
      </div>

    </div>
  );
};

export default MatchingQuestion;